import { Error as PhotoCheck, PhotoUploadResponse } from 'src/types/scanner'

/**
 * Compliance tests a passport-type photo cannot fail and still be used.
 * One list for the phone scanner's "Use" button and the desktop step's
 * "Use this photo": the analyzer's other tests (background, glasses, …)
 * are fixed by the background replacement or are a warning the customer
 * may accept.
 */
export const PHOTO_HARD_FAIL_TESTS: readonly string[] = [
	'TEST_NAKED',
	'TEST_WHITE_SHIRT',
	'TEST_PACIFIER',
	'TEST_MOUTH_CLOSED',
	'TEST_EYES_NOT_RED',
	'TEST_EYES_OPEN',
	'TEST_UNIFORM_LIGHTING',
	'TEST_NOT_TOO_DARK',
	'TEST_GAZE_FRONTAL',
	'TEST_FRONTAL',
	'TEST_NO_HOT_SPOTS'
]

export function photoHardFails(fails: PhotoCheck[] | null | undefined): PhotoCheck[] {
	return (fails ?? []).filter(fail => PHOTO_HARD_FAIL_TESTS.includes(fail.test_key))
}

/**
 * What to tell a customer whose uploaded file the analyzer refused. The
 * server's messages are written for a phone camera ("hold the camera
 * farther away"); on the desktop they are choosing a file, so each legacy
 * code (gov-api PassportPhotoService::ERROR_*) gets its own wording.
 */
export function photoUploadErrorMessage(response: PhotoUploadResponse | null | undefined): string {
	switch (response?.error_code) {
		case 2:
			return 'We couldn\'t find a face in that photo. Please choose a photo of just the applicant, facing the camera.'
		case 3:
			return 'That photo has more than one face in it. Please choose a photo with only the applicant.'
		case 4:
			// The server folds the analyzer's INSUFFICIENT_MARGIN and
			// LOW_RESOLUTION into this one code.
			return 'The applicant\'s head is too close to the edge of that photo, or the photo is too small. Please choose a larger photo with space around the head and shoulders — we\'ll frame it for you.'
		case 5:
			return 'We couldn\'t open that image. Please choose a JPEG or PNG photo.'
		default:
			return 'We couldn\'t process that photo. Please try a different one.'
	}
}
